import { useEffect } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { useForm, Controller } from "react-hook-form";
import { useResource } from "../../hooks/useResource.js";
import { FormShell } from "../../components/forms/FormShell.jsx";
import { FormSection } from "../../components/forms/FormSection.jsx";
import { SlugField } from "../../components/forms/SlugField.jsx";
import { Input } from "../../components/ui/Input.jsx";
import { Textarea } from "../../components/ui/Textarea.jsx";
import { Select } from "../../components/ui/Select.jsx";
import { Switch } from "../../components/ui/Switch.jsx";
import { Button } from "../../components/ui/Button.jsx";
import { PageLoader } from "../../components/ui/Spinner.jsx";
import { ImageUpload } from "../../components/upload/ImageUpload.jsx";
import { RichTextEditor } from "../../components/forms/RichTextEditor.jsx";

const DEFAULTS = {
  title: "",
  slug: "",
  icon: "",
  heroImage: "",
  shortDescription: "",
  content: "",
  ctaLabel: "",
  ctaLink: "",
  status: "draft",
  isFeatured: false,
  sortOrder: 0,
};

function buildPayload(values) {
  const out = { ...values };
  if (!out.slug) delete out.slug;
  if (out.sortOrder !== "") out.sortOrder = Number(out.sortOrder);
  return out;
}

export default function ServiceFormPage() {
  const navigate = useNavigate();
  const { id } = useParams();
  const isNew = id === "new" || !id;

  const { detail, create, update } = useResource("services", { toastLabel: "service" });
  const { data: existing, isLoading } = detail(isNew ? null : id);

  const form = useForm({ defaultValues: DEFAULTS });
  const {
    register,
    handleSubmit,
    control,
    setValue,
    watch,
    reset,
    formState: { errors, isSubmitting },
  } = form;

  useEffect(() => {
    if (existing) {
      reset({ ...DEFAULTS, ...existing });
    }
  }, [existing, reset]);

  useEffect(() => {
    document.title = isNew ? "New Service · Admin" : "Edit Service · Admin";
  }, [isNew]);

  const onSubmit = handleSubmit(async (values) => {
    const payload = buildPayload(values);
    if (isNew) {
      await new Promise((resolve, reject) => {
        create.mutate(payload, {
          onSuccess: (created) => {
            navigate(`/services/${created._id}`);
            resolve();
          },
          onError: reject,
        });
      });
    } else {
      await new Promise((resolve, reject) => {
        update.mutate({ id, data: payload }, { onSuccess: resolve, onError: reject });
      });
    }
  });

  if (!isNew && isLoading) return <PageLoader />;

  const watchedTitle = watch("title");
  const watchedSlug = watch("slug");

  return (
    <FormShell
      title={isNew ? "New Service" : `Edit "${existing?.title ?? ""}"`}
      backTo="/services"
      backLabel="Back to services"
      onSubmit={onSubmit}
      primaryAction={
        <Button type="submit" loading={isSubmitting}>
          {isNew ? "Create service" : "Save changes"}
        </Button>
      }
      secondaryAction={
        <Button type="button" variant="secondary" onClick={() => navigate("/services")}>
          Cancel
        </Button>
      }
      sidebar={
        <>
          <FormSection title="Status">
            <Select label="Status" {...register("status")}>
              <option value="draft">Draft</option>
              <option value="published">Published</option>
            </Select>
            <Controller
              control={control}
              name="isFeatured"
              render={({ field }) => (
                <Switch
                  label="Show on homepage strip"
                  hint="Featured services appear in the homepage marquee."
                  checked={field.value}
                  onCheckedChange={field.onChange}
                />
              )}
            />
            <Input type="number" label="Sort order" {...register("sortOrder")} />
          </FormSection>

          <FormSection title="CTA">
            <Input
              label="CTA label"
              hint="e.g. 'Request a quote'. Leave blank to hide the button."
              {...register("ctaLabel")}
            />
            <Input
              label="CTA link"
              hint="Internal path or full URL (e.g. /contact-us)."
              {...register("ctaLink")}
            />
          </FormSection>

          <FormSection title="Display">
            <Input
              label="Icon name"
              hint="lucide-react icon (home, hammer, map, calculator, briefcase) or /uploads path."
              {...register("icon")}
            />
          </FormSection>
        </>
      }
    >
      <FormSection title="Basics">
        <Input
          label="Title"
          required
          error={errors.title?.message}
          {...register("title", { required: "Title is required" })}
        />
        <SlugField
          value={watchedSlug}
          onChange={(v) => setValue("slug", v)}
          source={watchedTitle}
        />
        <Textarea
          label="Short description"
          rows={2}
          hint="Max 320 chars. Shown on the services list page + homepage strip."
          {...register("shortDescription")}
        />
      </FormSection>

      <FormSection title="Hero image">
        <Controller
          control={control}
          name="heroImage"
          render={({ field }) => (
            <ImageUpload
              value={field.value}
              onChange={field.onChange}
              subdir="services"
              hint="Recommended 16:9, ≥1600 px wide."
            />
          )}
        />
      </FormSection>

      <FormSection
        title="Long-form content"
        description="The body of the service detail page. Supports headings, lists, links, and images."
      >
        <Controller
          control={control}
          name="content"
          render={({ field }) => (
            <RichTextEditor value={field.value} onChange={field.onChange} subdir="services" />
          )}
        />
      </FormSection>
    </FormShell>
  );
}
